import { ArrayPath } from '@the-platform-company/path-utilities'
import { useCallback, useContext, useEffect, useState } from 'react'
import { StateContext } from './state-provider'
import { IViewState, State } from './types'
import { useBrowserState } from './use-browser-state'
import { getStatePath } from './utils'

export const createWithViewState =
  (viewId: string) =>
  (state: State): State => ({
    ...(state[viewId] as State),
    ...state,
  })

export const useViewState = (): IViewState => {
  const stateContext = useContext(StateContext)
  if (!stateContext) {
    throw new Error('useViewState must be used within a StateProvider')
  }

  const setState = useCallback(
    (namespace: string) => (path: string | ArrayPath, value: unknown) => {
      stateContext.store.setState(getStatePath(path, namespace), value)
      return value
    },
    [stateContext.store]
  )

  return {
    setState,
  }
}

export const useGetViewState = (): { getState: () => State } => {
  const stateContext = useContext(StateContext)
  if (!stateContext) {
    throw new Error('useViewState must be used within a StateProvider')
  }
  const browserState = useBrowserState()

  const getState = useCallback(
    () => ({
      ...browserState,
      ...stateContext.store.getState(),
    }),
    [browserState, stateContext.store]
  )

  return { getState }
}

export const useFullViewState = (): State => {
  const stateContext = useContext(StateContext)
  const { getState } = useGetViewState()

  const [localState, setLocalState] = useState(() => getState())

  useEffect(
    () => stateContext?.store.subscribe(() => setLocalState(getState())),
    [getState, stateContext?.store]
  )

  return localState
}

export const useStore = () => {
  const stateContext = useContext(StateContext)

  if (!stateContext) {
    throw new Error('useStore must be used within a StateProvider')
  }

  return stateContext.store
}
